import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sparkles, ChevronRight, ChevronLeft, Check, X, Loader2,
  FileText, Target, Layers, FolderKanban, GitBranch, Flag,
  Upload, RefreshCw, AlertTriangle, HelpCircle, ArrowRight,
  CheckCircle, Circle, Edit2, Trash2, Plus, Save, Wand2, Info
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SpecUploadModal from '../components/SpecUploadModal';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonCard } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';

// ==========================================
// WIZARD STEPS CONFIG
// ==========================================

const WIZARD_STEPS = [
  { id: 1, name: 'Project Context', icon: HelpCircle, description: 'Define project details' },
  { id: 2, name: 'Extract Requirements', icon: Upload, description: 'Upload spec or enter manually' },
  { id: 3, name: 'Refine Requirements', icon: Sparkles, description: 'AI-assisted improvements' },
  { id: 4, name: 'Organize Hierarchy', icon: Layers, description: 'Theme/Epic organization' },
  { id: 5, name: 'Work Packages', icon: FolderKanban, description: 'Template-driven packages' },
  { id: 6, name: 'Generate WBS', icon: GitBranch, description: 'Automated WBS creation' },
  { id: 7, name: 'Create Baseline', icon: Flag, description: 'Finalize for approval' },
];

// ==========================================
// STEP INDICATOR COMPONENT
// ==========================================

const StepIndicator = ({ steps, currentStep, onStepClick }) => {
  return (
    <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
      {steps.map((step, index) => {
        const isActive = step.id === currentStep;
        const isCompleted = step.id < currentStep;
        const Icon = step.icon;
        
        return (
          <React.Fragment key={step.id}>
            <button
              onClick={() => isCompleted && onStepClick(step.id)}
              disabled={!isCompleted}
              className={`flex flex-col items-center min-w-[100px] transition-all ${
                isCompleted ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all ${
                isActive 
                  ? 'bg-[var(--clr-brand-primary)] text-white shadow-lg ring-4 ring-purple-100'
                  : isCompleted 
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
              }`}>
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
              </div>
              <span className={`text-xs font-medium text-center ${
                isActive ? 'text-[var(--clr-brand-primary)]' : 'text-gray-500'
              }`}>
                {step.name}
              </span>
            </button>
            
            {index < steps.length - 1 && (
              <div className={`flex-1 h-1 mx-2 rounded ${
                step.id < currentStep ? 'bg-green-500' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ==========================================
// STEP 1: PROJECT CONTEXT
// ==========================================

const Step1Context = ({ session, onUpdate, onNext }) => {
  const [projectName, setProjectName] = useState(session?.project_name || '');
  const [projectDescription, setProjectDescription] = useState(session?.project_description || '');
  const [questions, setQuestions] = useState(session?.step_data?.step_1?.questions || []);
  const [answers, setAnswers] = useState(session?.step_data?.step_1?.answers || {});
  const [loading, setLoading] = useState(false);

  const handleGenerateQuestions = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/api/scoping/wizard/session/${session.id}/context-questions`, {});
      setQuestions(response.data.questions || []);
    } catch (err) {
      toast.error('Failed to generate questions');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    await onUpdate({
      step: 2,
      step_data: { project_name: projectName, project_description: projectDescription, answers }
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Details</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter project name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              data-testid="wizard-project-name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              placeholder="Describe the project scope and objectives"
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              data-testid="wizard-project-description"
            />
          </div>
        </div>
      </div>

      {/* Clarifying Questions */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Clarifying Questions</h3>
          <button
            onClick={handleGenerateQuestions}
            disabled={loading || !projectName}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            Generate Questions
          </button>
        </div>

        {questions.length > 0 ? (
          <div className="space-y-4">
            {questions.map((q, i) => (
              <div key={q.id || i} className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800 mb-2">{q.question}</p>
                {q.type === 'text' && (
                  <input
                    type="text"
                    value={answers[q.id] || ''}
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Your answer..."
                  />
                )}
                {q.type === 'select' && (
                  <select
                    value={answers[q.id] || ''}
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select an option</option>
                    {q.options?.map((opt, j) => (
                      <option key={j} value={opt}>{opt}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">
            Click "Generate Questions" to get AI-powered clarifying questions based on your project details.
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleNext}
          disabled={!projectName}
          className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          data-testid="wizard-next-btn"
        >
          Next: Extract Requirements
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ==========================================
// STEP 2: EXTRACT REQUIREMENTS
// ==========================================

const Step2Extract = ({ session, onUpdate, onNext, onBack, tenantId, userId }) => {
  const [showUpload, setShowUpload] = useState(false);
  const [items, setItems] = useState(session?.extracted_items || []);
  const [newItemName, setNewItemName] = useState('');

  const handleUploadComplete = (result) => {
    setItems(prev => [...prev, ...(result.items || [])]);
    setShowUpload(false);
    toast.success(`Added ${result.imported_count || result.items?.length || 0} items`);
  };

  const handleAddManual = () => {
    if (!newItemName.trim()) return;
    setItems(prev => [...prev, {
      name: newItemName,
      description: '',
      hierarchy_type: 'requirement',
      category: 'Functional',
      confidence: 1.0
    }]);
    setNewItemName('');
  };

  const handleRemoveItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    await onUpdate({ step: 3, extracted_items: items });
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          data-testid="wizard-upload-spec-btn"
        >
          <Upload className="w-5 h-5" />
          Upload Spec Document
        </button>
        <span className="text-gray-400">or</span>
        <div className="flex-1 flex items-center gap-2">
          <input
            type="text"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            placeholder="Enter requirement manually..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
            onKeyDown={(e) => e.key === 'Enter' && handleAddManual()}
          />
          <button
            onClick={handleAddManual}
            disabled={!newItemName.trim()}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Items List */}
      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900">
            Extracted Items ({items.length})
          </h3>
        </div>
        
        {items.length > 0 ? (
          <div className="max-h-[400px] overflow-y-auto">
            {items.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-3 border-b last:border-0 hover:bg-gray-50 group"
              >
                <span className={`px-2 py-0.5 text-xs rounded ${
                  item.hierarchy_type === 'theme' ? 'bg-purple-100 text-purple-700' :
                  item.hierarchy_type === 'epic' ? 'bg-blue-100 text-blue-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {item.hierarchy_type}
                </span>
                <span className="flex-1 text-sm font-medium text-gray-800">{item.name}</span>
                <button
                  onClick={() => handleRemoveItem(index)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No items yet. Upload a spec document or add items manually.</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={items.length === 0}
          className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          data-testid="wizard-next-btn"
        >
          Next: Refine Requirements
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Upload Modal */}
      <SpecUploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        tenantId={tenantId}
        projectId={null}
        userId={userId}
        onImportComplete={handleUploadComplete}
      />
    </div>
  );
};

// ==========================================
// STEP 3: REFINE REQUIREMENTS (Simplified)
// ==========================================

const Step3Refine = ({ session, onUpdate, onNext, onBack }) => {
  const items = session?.extracted_items || [];
  
  const handleNext = async () => {
    await onUpdate({ step: 4 });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
        <div className="flex items-start gap-4">
          <Sparkles className="w-8 h-8 text-purple-600" />
          <div>
            <h3 className="font-semibold text-purple-900 mb-1">AI-Assisted Refinement</h3>
            <p className="text-purple-700 text-sm">
              Use the AI Assistant to improve requirement clarity, generate acceptance criteria,
              and identify risks for each item. You can access the AI Assistant after completing 
              the wizard from the main workspace.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-gray-900 mb-4">
          Requirements to Refine ({items.length})
        </h3>
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {items.map((item, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-800">{item.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        <button onClick={handleNext} className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90">
          Next: Organize Hierarchy <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ==========================================
// STEP 4: ORGANIZE HIERARCHY
// ==========================================

const Step4Organize = ({ session, onUpdate, onNext, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [organized, setOrganized] = useState(session?.organized_hierarchy || []);
  const [reasoning, setReasoning] = useState('');

  const handleOrganize = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/api/scoping/wizard/session/${session.id}/organize-hierarchy`, {});
      setOrganized(response.data.organized_hierarchy || []);
      setReasoning(response.data.reasoning || '');
      toast.success('Hierarchy organized by AI');
    } catch (err) {
      toast.error('Failed to organize hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    await onUpdate({ step: 5, organized_hierarchy: organized });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Organize into Themes & Epics</h3>
        <button
          onClick={handleOrganize}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
          AI Organize
        </button>
      </div>

      {reasoning && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm text-blue-700">{reasoning}</p>
        </div>
      )}

      <div className="bg-white rounded-xl border">
        {organized.length > 0 ? (
          <div className="p-4 space-y-4">
            {organized.map((theme, ti) => (
              <div key={ti} className="border rounded-lg overflow-hidden">
                <div className="bg-purple-50 p-3 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-900">{theme.name}</span>
                </div>
                <div className="p-3 space-y-2">
                  {theme.children?.map((epic, ei) => (
                    <div key={ei} className="ml-4 border-l-2 border-blue-200 pl-3">
                      <div className="flex items-center gap-2 text-blue-700 font-medium mb-1">
                        <Target className="w-4 h-4" />
                        {epic.name}
                      </div>
                      <div className="ml-4 space-y-1">
                        {epic.children?.map((req, ri) => (
                          <div key={ri} className="flex items-center gap-2 text-sm text-gray-600">
                            <Circle className="w-2 h-2 fill-current" />
                            {req.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Click "AI Organize" to automatically organize requirements into a hierarchy.</p>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        <button 
          onClick={handleNext}
          disabled={organized.length === 0}
          className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
        >
          Next: Work Packages <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ==========================================
// STEP 5-7: SIMPLIFIED FINAL STEPS
// ==========================================

const Step5Packages = ({ session, onUpdate, onNext, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState(session?.work_packages || []);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/api/scoping/wizard/session/${session.id}/generate-work-packages`, {});
      setPackages(response.data.work_packages || []);
      toast.success(`Generated ${response.data.total_packages} work packages`);
    } catch (err) {
      toast.error('Failed to generate work packages');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    await onUpdate({ step: 6, work_packages: packages });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Generate Work Packages</h3>
        <button onClick={handleGenerate} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
          Generate Packages
        </button>
      </div>

      <div className="bg-white rounded-xl border p-6">
        {packages.length > 0 ? (
          <div className="space-y-3">
            {packages.slice(0, 10).map((wp, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800">Requirement #{wp.requirement_index + 1}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {wp.packages?.map((pkg, j) => (
                    <span key={j} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                      {pkg.phase}: {pkg.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">Click "Generate Packages" to create work packages from templates.</p>
        )}
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        <button onClick={handleNext} className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90">
          Next: Generate WBS <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const Step6WBS = ({ session, onUpdate, onNext, onBack }) => {
  const handleNext = async () => {
    await onUpdate({ step: 7 });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="bg-green-50 rounded-xl p-6 border border-green-200">
        <div className="flex items-start gap-4">
          <GitBranch className="w-8 h-8 text-green-600" />
          <div>
            <h3 className="font-semibold text-green-900 mb-1">WBS Generation Ready</h3>
            <p className="text-green-700 text-sm">
              The WBS will be automatically generated when you create the baseline in the next step.
              It will use your requirements and work packages to create a complete Work Breakdown Structure.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        <button onClick={handleNext} className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90">
          Next: Create Baseline <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const Step7Baseline = ({ session, onComplete, onBack, tenantId, userId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Create project first - use correct API signature: (tenantId, projectData)
      const projectRes = await api.createScopingProject(tenantId, {
        name: session.project_name || 'Wizard Project',
        description: session.project_description || '',
        status: 'active',
      });
      
      // API returns project directly
      const projectId = projectRes.data.id;
      
      // Import requirements if any exist
      if (session.extracted_items?.length > 0) {
        try {
          await api.importExtractedRequirements({
            tenant_id: tenantId,
            project_id: projectId,
            items: JSON.stringify(session.extracted_items),
            user_id: userId,
          });
        } catch (importErr) {
          console.warn('Failed to import requirements:', importErr);
          // Continue anyway - requirements can be added later
        }
      }
      
      // Create baseline - use correct API signature: (tenantId, userId, baseline)
      const baselineRes = await api.createBaseline(tenantId, userId, {
        project_id: projectId,
        name: 'V1',
        version: 'V1',
        status: 'draft',
      });
      
      // Extract baseline ID from response (API returns {baseline: {...}})
      const baselineId = baselineRes.data?.baseline?.id || baselineRes.data?.id;
      
      // Complete wizard session
      try {
        await api.post(`/api/scoping/wizard/session/${session.id}/complete`, null, {
          params: { project_id: projectId, baseline_id: baselineId || 'none' }
        });
      } catch (completeErr) {
        console.warn('Failed to mark wizard complete:', completeErr);
        // Continue anyway - project is created
      }
      
      toast.success('Wizard completed! Project and baseline created.');
      navigate(`/scope/workspace?project=${projectId}`);
    } catch (err) {
      console.error('Failed to complete wizard:', err);
      toast.error(`Failed to complete wizard: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Wizard Summary</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-700">
              {session.extracted_items?.length || 0}
            </p>
            <p className="text-sm text-purple-600">Requirements</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-700">
              {session.organized_hierarchy?.length || 0}
            </p>
            <p className="text-sm text-blue-600">Themes</p>
          </div>
        </div>

        <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-900 mb-2">Ready to Create:</h4>
          <ul className="space-y-1 text-sm text-green-700">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              New Project: {session.project_name}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Import {session.extracted_items?.length || 0} requirements
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Create baseline V1 (Draft)
            </li>
          </ul>
        </div>
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        <button
          onClick={handleComplete}
          disabled={loading}
          className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold"
          data-testid="wizard-complete-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
          Complete Wizard & Create Project
        </button>
      </div>
    </div>
  );
};

// ==========================================
// MAIN WIZARD PAGE
// ==========================================

const ScopingWizardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [existingProject, setExistingProject] = useState(null);
  const [existingRequirements, setExistingRequirements] = useState([]);

  const tenantId = user?.tenant?.id || user?.tenant_id;
  const userId = user?.id || user?._id;
  
  // Check if we're continuing with an existing project
  const projectId = searchParams.get('project');

  useEffect(() => {
    initializeSession();
  }, [tenantId, userId, projectId]);

  const initializeSession = async () => {
    if (!tenantId || !userId) return;
    
    setLoading(true);
    try {
      let sessionData = null;
      
      // If projectId is provided, load existing project data
      if (projectId) {
        try {
          console.log('[Wizard] Loading existing project:', projectId);
          
          // Load project details
          const projectRes = await api.getProject(tenantId, projectId);
          const project = projectRes.data;
          setExistingProject(project);
          console.log('[Wizard] Project loaded:', project.name);
          
          // Load existing requirements
          const reqRes = await api.listRequirements(tenantId, projectId, { include_tree: true });
          const existingReqs = reqRes.data.requirements || [];
          setExistingRequirements(existingReqs);
          console.log('[Wizard] Loaded', existingReqs.length, 'existing requirements');
          
          // Create wizard session with existing data
          const response = await api.post('/api/scoping/wizard/session', {
            tenant_id: tenantId,
            user_id: userId,
            project_id: projectId,
            project_name: project.name,
            project_description: project.description,
            // Pre-populate extracted items from existing requirements
            extracted_items: flattenRequirementsForWizard(existingReqs),
          });
          
          sessionData = {
            ...response.data,
            project_name: project.name,
            project_description: project.description,
            extracted_items: flattenRequirementsForWizard(existingReqs),
            existing_project_id: projectId,
          };
          
          // Determine which step to start at based on existing data
          const startStep = determineStartStep(existingReqs);
          setCurrentStep(startStep);
          
          toast.success(`Loaded existing project: ${project.name}`);
        } catch (err) {
          console.warn('[Wizard] Failed to load existing project:', err);
          // Fall through to create new session
        }
      }
      
      // If no existing project loaded, create new session
      if (!sessionData) {
        console.log('[Wizard] Creating new session');
        const response = await api.post('/api/scoping/wizard/session', {
          tenant_id: tenantId,
          user_id: userId,
        });
        sessionData = response.data;
        setCurrentStep(response.data.current_step || 1);
      }
      
      setSession(sessionData);
    } catch (err) {
      console.error('[Wizard] Failed to initialize session:', err);
      toast.error('Failed to start wizard');
    } finally {
      setLoading(false);
    }
  };

  // Helper to flatten requirements tree for wizard
  const flattenRequirementsForWizard = (requirements, result = []) => {
    requirements.forEach(item => {
      result.push({
        name: item.name,
        description: item.description,
        hierarchy_type: item.hierarchy_type,
        category: item.category,
        acceptance_criteria: item.acceptance_criteria || [],
        risks: item.risks || [],
        constraints: item.constraints || [],
        priority: item.priority,
        existing_id: item.id, // Track that this came from an existing requirement
      });
      if (item.children?.length) {
        flattenRequirementsForWizard(item.children, result);
      }
    });
    return result;
  };

  // Determine which wizard step to start at based on existing data
  const determineStartStep = (requirements) => {
    if (!requirements || requirements.length === 0) return 1;
    
    // Count what we have
    const themes = requirements.filter(r => r.hierarchy_type === 'theme');
    const epics = requirements.filter(r => r.hierarchy_type === 'epic');
    const reqs = requirements.filter(r => r.hierarchy_type === 'requirement');
    
    // If we have organized hierarchy (themes/epics), start at step 4
    if (themes.length > 0 || epics.length > 0) return 4;
    
    // If we have requirements but no hierarchy, start at step 3 (refine)
    if (reqs.length > 0) return 3;
    
    // If we have some items, start at step 2 (extract)
    return 2;
  };

  const handleUpdateStep = async (updates) => {
    if (!session?.id) return;
    
    try {
      const response = await api.put(`/api/scoping/wizard/session/${session.id}/step`, updates);
      setSession(response.data);
    } catch (err) {
      console.error('Failed to update step:', err);
    }
  };

  const handleNext = () => setCurrentStep(prev => Math.min(prev + 1, 7));
  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 1));
  const handleStepClick = (step) => setCurrentStep(step);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <SkeletonCard />
      </div>
    );
  }

  return (
    <PageErrorBoundary>
      <div className="max-w-4xl mx-auto p-6">
        {/* Continue Existing Project Banner */}
        {existingProject && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">
                  Continuing with: {existingProject.name}
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  {existingRequirements.length} existing requirements loaded. You can review and enhance them, or add more through the wizard.
                </p>
                <button
                  onClick={() => navigate(`/scope/workspace?project=${projectId}`)}
                  className="text-sm text-blue-600 hover:text-blue-800 underline mt-2"
                >
                  Return to Workspace
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Scoping Wizard</h1>
          </div>
          <p className="text-gray-600">
            AI-guided workflow: Upload spec → Extract → Organize → Generate WBS → Create Baseline
          </p>
        </div>

        {/* Step Indicator */}
        <StepIndicator
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />

        {/* Step Content */}
        <div className="mt-6">
          {currentStep === 1 && (
            <Step1Context session={session} onUpdate={handleUpdateStep} onNext={handleNext} />
          )}
          {currentStep === 2 && (
            <Step2Extract session={session} onUpdate={handleUpdateStep} onNext={handleNext} onBack={handleBack} tenantId={tenantId} userId={userId} />
          )}
          {currentStep === 3 && (
            <Step3Refine session={session} onUpdate={handleUpdateStep} onNext={handleNext} onBack={handleBack} />
          )}
          {currentStep === 4 && (
            <Step4Organize session={session} onUpdate={handleUpdateStep} onNext={handleNext} onBack={handleBack} />
          )}
          {currentStep === 5 && (
            <Step5Packages session={session} onUpdate={handleUpdateStep} onNext={handleNext} onBack={handleBack} />
          )}
          {currentStep === 6 && (
            <Step6WBS session={session} onUpdate={handleUpdateStep} onNext={handleNext} onBack={handleBack} />
          )}
          {currentStep === 7 && (
            <Step7Baseline session={session} onComplete={() => {}} onBack={handleBack} tenantId={tenantId} userId={userId} />
          )}
        </div>

        {/* Exit */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/scope/workspace')}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Exit Wizard
          </button>
        </div>
      </div>
    </PageErrorBoundary>
  );
};

export default ScopingWizardPage;
